import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LocationStatus from "../components/LocationStatus";
import { Location, getStatusInfo } from "../data/storeHours";
import {
  trackCallClick,
  trackReservationClick,
  trackEmailClick,
} from "../utils/analytics";
import "../styles/ReservationsPage.css";

const ReservationsPage: React.FC = () => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language as "en" | "fr";

  // State for open/closed status (updates every minute)
  const [montrealStatus, setMontrealStatus] = useState(() =>
    getStatusInfo("montreal", currentLang),
  );
  const [delsonStatus, setDelsonStatus] = useState(() =>
    getStatusInfo("delson", currentLang),
  );

  // Update status every minute and when language changes
  useEffect(() => {
    const updateStatuses = () => {
      setMontrealStatus(getStatusInfo("montreal", currentLang));
      setDelsonStatus(getStatusInfo("delson", currentLang));
    };

    // Update immediately when language changes
    updateStatuses();

    // Update every minute
    const interval = setInterval(updateStatuses, 60000);

    return () => clearInterval(interval);
  }, [currentLang]);

  const locationData = {
    montreal: {
      name: currentLang === "en" ? "MONTREAL" : "MONTRÉAL",
      address:
        currentLang === "fr"
          ? "2532 rue Ste-Catherine E\nMontréal, QC H2K 2K2"
          : "2532 St Catherine St E\nMontreal, QC H2K 2K2",
      phone: "514-527-2532",
      phoneLink: "tel:5145272532",
      hours: {
        en: "Tue–Sun 11AM–9PM, Mon Closed",
        fr: "Mar-Dim 11H–21H, Lun Fermé",
      },
    },
    delson: {
      name: "DELSON",
      address: "35 Boulevard Georges-Gagné Sud\nDelson, QC J5B 2E4",
      phone: "450-632-5320",
      phoneLink: "tel:4506325320",
      hours: {
        en: "Thu–Sun 11AM–9PM, Wed 4PM–8:30PM, Mon-Tue Closed",
        fr: "Jeu-Dim 11H–21H, Mer 16H–20H30, Lun-Mar Fermé",
      },
    },
  };

  const eventTypes =
    currentLang === "fr"
      ? [
          {
            icon: "🎂",
            title: "Anniversaires",
            description:
              "Célébrez votre journée spéciale avec une cuisine grecque authentique",
          },
          {
            icon: "💒",
            title: "Mariages",
            description: "Réceptions intimes et célébrations de mariage",
          },
          {
            icon: "👶",
            title: "Baptêmes",
            description:
              "Rassemblements familiaux traditionnels pour des occasions spéciales",
          },
          {
            icon: "🎉",
            title: "Événements d'entreprise",
            description:
              "Réunions d'affaires, dîners d'équipe et événements corporatifs",
          },
          {
            icon: "👨‍👩‍👧‍👦",
            title: "Réunions de famille",
            description: "Grands rassemblements familiaux et célébrations",
          },
          {
            icon: "🍽️",
            title: "Groupes privés",
            description:
              "Dîners privés et occasions spéciales pour tout groupe",
          },
        ]
      : [
          {
            icon: "🎂",
            title: "Birthdays",
            description:
              "Celebrate your special day with authentic Greek cuisine",
          },
          {
            icon: "💒",
            title: "Weddings",
            description: "Intimate wedding receptions and celebrations",
          },
          {
            icon: "👶",
            title: "Baptisms",
            description: "Traditional family gatherings for special occasions",
          },
          {
            icon: "🎉",
            title: "Corporate Events",
            description:
              "Business meetings, team dinners, and corporate events",
          },
          {
            icon: "👨‍👩‍👧‍👦",
            title: "Family Reunions",
            description: "Large family gatherings and celebrations",
          },
          {
            icon: "🍽️",
            title: "Private Groups",
            description: "Private dinners and special occasions for any group",
          },
        ];

  const features =
    currentLang === "fr"
      ? [
          {
            icon: "🍷",
            title: "Apportez votre vin",
            description:
              "Apportez vos bouteilles préférées sans frais de bouchon",
          },
          {
            icon: "📋",
            title: "Menu personnalisé",
            description: "Créez un menu sur mesure adapté à votre événement",
          },
          {
            icon: "👥",
            title: "Groupes flexibles",
            description:
              "Accueil de petits groupes intimes aux grandes célébrations",
          },
        ]
      : [
          {
            icon: "🍷",
            title: "Bring Your Own Wine",
            description: "Bring your favorite bottles with no corkage fee",
          },
          {
            icon: "📋",
            title: "Custom Menu",
            description: "Create a tailored menu suited to your event",
          },
          {
            icon: "👥",
            title: "Flexible Groups",
            description:
              "Accommodating small intimate groups to large celebrations",
          },
        ];

  return (
    <div className="reservations-page">
      <section className="reservations-hero">
        <div className="reservations-hero-content">
          <h1 className="reservations-hero-title">
            {currentLang === "fr"
              ? "RÉSERVATIONS & ÉVÉNEMENTS"
              : "RESERVATIONS & EVENTS"}
          </h1>
          <p className="reservations-hero-description">
            {currentLang === "fr"
              ? "Célébrez vos moments spéciaux avec nous. Notre espace chaleureux est parfait pour les groupes et les événements privés."
              : "Celebrate your special moments with us. Our warm space is perfect for groups and private events."}
          </p>
        </div>
      </section>

      {/* Features Section */}
      <section className="reservations-features">
        <div className="container">
          <div className="reservations-features-grid">
            {features.map((feature, index) => (
              <div key={index} className="reservations-feature">
                <span className="reservations-feature-icon">
                  {feature.icon}
                </span>
                <h3 className="reservations-feature-title">{feature.title}</h3>
                <p className="reservations-feature-description">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Event Types Section */}
      <section className="reservations-events">
        <div className="container">
          <h2 className="reservations-section-title">
            {currentLang === "fr" ? "TYPES D'ÉVÉNEMENTS" : "EVENT TYPES"}
          </h2>
          <p className="reservations-section-subtitle">
            {currentLang === "fr"
              ? "Nous accueillons tous types de célébrations et rassemblements"
              : "We welcome all types of celebrations and gatherings"}
          </p>
          <div className="reservations-events-grid">
            {eventTypes.map((event, index) => (
              <div key={index} className="reservations-event-card">
                <span className="reservations-event-icon">{event.icon}</span>
                <h3 className="reservations-event-title">{event.title}</h3>
                <p className="reservations-event-description">
                  {event.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="reservations-contact">
        <div className="container">
          <h2 className="reservations-section-title">
            {currentLang === "fr" ? "RÉSERVER MAINTENANT" : "BOOK NOW"}
          </h2>
          <p className="reservations-section-subtitle">
            {currentLang === "fr"
              ? "Contactez-nous par téléphone ou courriel pour réserver votre événement"
              : "Contact us by phone or email to book your event"}
          </p>

          <div className="reservations-locations-grid">
            {/* Montreal Location */}
            <div className="reservations-location-card">
              <h2 className="reservations-location-title">
                {locationData.montreal.name}
              </h2>
              <LocationStatus
                location={"montreal" as Location}
                lang={currentLang}
                hours={locationData.montreal.hours[currentLang]}
                classPrefix="reservations-location"
              />
              <div className="reservations-location-address">
                {locationData.montreal.address
                  .split("\n")
                  .map((line, index) => (
                    <React.Fragment key={index}>
                      {line}
                      {index <
                        locationData.montreal.address.split("\n").length -
                          1 && <br />}
                    </React.Fragment>
                  ))}
              </div>
              <a
                href={locationData.montreal.phoneLink}
                className="reservations-location-phone"
              >
                {locationData.montreal.phone}
              </a>
              <div className="reservations-location-actions">
                {montrealStatus.isOpen ? (
                  <a
                    href={locationData.montreal.phoneLink}
                    className="reservations-btn reservations-btn-primary"
                    onClick={() => {
                      trackCallClick("montreal", locationData.montreal.phone);
                      trackReservationClick("montreal");
                    }}
                  >
                    {currentLang === "fr"
                      ? "APPELER POUR RÉSERVER"
                      : "CALL TO RESERVE"}
                  </a>
                ) : (
                  <button
                    className="reservations-btn reservations-btn-primary reservations-btn-disabled"
                    disabled
                  >
                    {currentLang === "fr"
                      ? "APPELER POUR RÉSERVER"
                      : "CALL TO RESERVE"}
                  </button>
                )}
                <Link
                  to="/montreal"
                  className="reservations-btn reservations-btn-secondary"
                >
                  {currentLang === "fr"
                    ? "VOIR L'EMPLACEMENT"
                    : "VIEW LOCATION"}
                </Link>
              </div>
            </div>

            {/* Delson Location */}
            <div className="reservations-location-card">
              <h2 className="reservations-location-title">
                {locationData.delson.name}
              </h2>
              <LocationStatus
                location={"delson" as Location}
                lang={currentLang}
                hours={locationData.delson.hours[currentLang]}
                classPrefix="reservations-location"
              />
              <div className="reservations-location-address">
                {locationData.delson.address.split("\n").map((line, index) => (
                  <React.Fragment key={index}>
                    {line}
                    {index <
                      locationData.delson.address.split("\n").length - 1 && (
                      <br />
                    )}
                  </React.Fragment>
                ))}
              </div>
              <a
                href={locationData.delson.phoneLink}
                className="reservations-location-phone"
              >
                {locationData.delson.phone}
              </a>
              <div className="reservations-location-actions">
                {delsonStatus.isOpen ? (
                  <a
                    href={locationData.delson.phoneLink}
                    className="reservations-btn reservations-btn-primary"
                    onClick={() => {
                      trackCallClick("delson", locationData.delson.phone);
                      trackReservationClick("delson");
                    }}
                  >
                    {currentLang === "fr"
                      ? "APPELER POUR RÉSERVER"
                      : "CALL TO RESERVE"}
                  </a>
                ) : (
                  <button
                    className="reservations-btn reservations-btn-primary reservations-btn-disabled"
                    disabled
                  >
                    {currentLang === "fr"
                      ? "APPELER POUR RÉSERVER"
                      : "CALL TO RESERVE"}
                  </button>
                )}
                <Link
                  to="/delson"
                  className="reservations-btn reservations-btn-secondary"
                >
                  {currentLang === "fr"
                    ? "VOIR L'EMPLACEMENT"
                    : "VIEW LOCATION"}
                </Link>
              </div>
            </div>
          </div>

          {/* Email Contact */}
          <div className="reservations-email">
            <p>
              {currentLang === "fr"
                ? "Ou envoyez-nous un courriel à"
                : "Or send us an email at"}
            </p>
            <a
              href="mailto:lacavagrecque@hotmail.com"
              className="reservations-email-link"
              onClick={() => trackEmailClick("reservations_page")}
            >
              lacavagrecque@hotmail.com
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ReservationsPage;
